import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Swords, Trophy, User, LogOut, Volume2, VolumeX } from 'lucide-react';
import { useAuthStore, useMatchmakingStore, useSettingsStore } from '@fog-chess/core';
import { GlobalMatchmakingListener } from '@/components/GlobalMatchmakingListener';
import { Toaster } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { audioController } from '@fog-chess/core';

/**
 * RootLayout - top-level authenticated shell.
 *
 * Hosts exactly-once concerns that must outlive screen navigation:
 *  - GlobalMatchmakingListener (Mandate B): auto-routes to /game/:matchId
 *    on `match:found` regardless of the user's current screen.
 *  - Toaster: global toast container reachable by any screen via `toast()`.
 *  - Persistent app bar with the user's avatar, FTPES chip and logout.
 */
const NAV_ITEMS = [
  { to: '/', label: 'Home', icon: Swords, end: true },
  { to: '/quick-match', label: 'Quick Match', icon: Swords, end: false },
  { to: '/leaderboard', label: 'Leaderboard', icon: Trophy, end: false },
  { to: '/profile', label: 'Profile', icon: User, end: false },
] as const;

export function RootLayout() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const resetMatchmaking = useMatchmakingStore((s) => s.reset);
  const soundEnabled = useSettingsStore((s) => s.soundEnabled);
  const toggleSound = useSettingsStore((s) => s.toggleSound);

  const handleLogout = () => {
    // Tear down the singleton socket + clear ephemeral stores so a later
    // login session starts from a clean slate.
    import('@fog-chess/core').then(({ disconnectSocket }) => {
      disconnectSocket();
    });
    clearAuth();
    resetMatchmaking();
    navigate('/auth', { replace: true });
  };

  const initials = (user?.username ?? '?')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <GlobalMatchmakingListener />
      <Toaster />

      <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4">
          <NavLink to="/" className="flex items-center gap-2">
            <motion.span
              initial={{ rotate: -8, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-sm"
            >
              <Swords className="h-5 w-5" />
            </motion.span>
            <span className="text-lg font-bold tracking-tight">
              Fog Chess
            </span>
          </NavLink>

          <nav className="hidden items-center gap-1 md:flex">
            {NAV_ITEMS.map(({ to, label, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  cn(
                    'rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-secondary text-secondary-foreground'
                      : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground',
                  )
                }
              >
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {/* Sound toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                audioController.unlockAudio();
                toggleSound();
              }}
              aria-label={soundEnabled ? 'Mute sounds' : 'Unmute sounds'}
              aria-pressed={soundEnabled}
              title={soundEnabled ? 'Mute' : 'Unmute'}
            >
              {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            </Button>
            <Avatar className="h-9 w-9 border border-border">
              <AvatarFallback className="bg-secondary text-xs font-semibold text-secondary-foreground">
                {initials}
              </AvatarFallback>
            </Avatar>
            <span className="hidden text-sm font-medium sm:inline">
              {user?.username ?? 'Player'}
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              aria-label="Log out"
              title="Log out"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Compact mobile nav strip */}
        <nav className="flex items-center gap-1 overflow-x-auto px-2 pb-2 md:hidden">
          {NAV_ITEMS.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors',
                  isActive
                    ? 'bg-secondary text-secondary-foreground'
                    : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground',
                )
              }
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}
